import { useRef, type ReactNode } from 'react';
import { Portal } from './Portal';
import { IconButton } from './IconButton';
import { useFocusTrap } from './useFocusTrap';
import './Sheet.css';

/**
 * DESIGN.md §9.1 `Sheet` (mobile) — the slide-over presentation AC-UI-8 and DESIGN.md §10 want
 * for the facet panel and inspector once they no longer fit as inline columns
 * (`resolveResponsiveLayout`'s `'sheet'` mode). Same focus-trap/`Esc`/restore-focus contract as
 * `Dialog` (DESIGN.md §6.1) — MVP-11 #28 replaced what used to be an independent ~80-line copy of
 * that effect with the shared `useFocusTrap.ts` hook both this and `Dialog.tsx` now call.
 */
export function Sheet({
  open,
  onClose,
  title,
  side = 'right',
  size = 'md',
  closeLabel,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  side?: 'right' | 'bottom';
  /** MVP-268: the guest page passes `'lg'` to grow the close button's box to the 44px guest floor
   * (DESIGN.md §6.3) — the App's own sheet (the Library facet panel) stays at the default `'md'`
   * (36px), which already clears its 24px floor with room to spare. `iconSize={20}` below pins the
   * X glyph at its current pixel size in both cases, so only the hit area changes. */
  size?: 'md' | 'lg';
  /** The close button's accessible name. Passed in rather than resolved with `useT()` here, for the
   * same reason as `SearchField`'s `clearLabel` — `ui/` primitives stay dictionary-free.
   *
   * MVP-357: required. MVP-272 Phase 1 left it optional with an English `?? 'Close'` fallback so the
   * Library facet panel could keep its English name until Phase 4 reached it. Phase 4 reached it;
   * the caller that was relying on the fallback now passes `t('common.close')`. */
  closeLabel: string;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleId = useRef(`sheet-title-${Math.random().toString(36).slice(2)}`).current;

  useFocusTrap(panelRef, open, onClose);

  if (!open) return null;

  return (
    <Portal>
      <div className={`sheet-scrim sheet-scrim--${side}`} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div
          className={`sheet-panel sheet-panel--${side}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          ref={panelRef}
          tabIndex={-1}
        >
          <div className="sheet-panel__header">
            {/* MVP-367: an `<h2>` for the same reason as `Dialog.tsx`'s — this is the same
                `role="dialog"` region, reached the same way, and it had the same nothing in it to
                navigate by. */}
            <h2 id={titleId} className="text-subtitle">
              {title}
            </h2>
            <IconButton icon="close" label={closeLabel} size={size} iconSize={20} onClick={onClose} />
          </div>
          <div className="sheet-panel__body">{children}</div>
        </div>
      </div>
    </Portal>
  );
}
